import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, Sparkles, ArrowRight, RotateCcw, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const GENDERS = [
  { key: "male", label: "Male" },
  { key: "female", label: "Female" },
  { key: "other", label: "Other" },
];
const FITS = [
  { key: "regular", label: "Regular", desc: "True to size" },
  { key: "relaxed", label: "Relaxed", desc: "Roomy & comfy" },
];

function toCm(val, unit, inches) {
  const ft = parseFloat(val || 0);
  const inch = parseFloat(inches || 0);
  return ft * 30.48 + inch * 2.54;
}

const Segmented = ({ options, value, onChange, testPrefix }) => (
  <div className={`grid gap-2 ${options.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
    {options.map((o) => {
      const active = value === o.key;
      return (
        <button
          key={o.key}
          type="button"
          data-testid={`${testPrefix}-${o.key}`}
          onClick={() => onChange(o.key)}
          className={`flex flex-col items-start px-3 py-3 border text-left transition-colors duration-200 ${
            active
              ? "bg-white text-black border-white"
              : "bg-[#141414] text-white border-[#333] hover:border-white/60"
          }`}
        >
          <span className="text-sm font-bold">{o.label}</span>
          {o.desc && (
            <span className={`text-[10px] mt-0.5 ${active ? "text-black/60" : "text-white/40"}`}>
              {o.desc}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

const ConfidenceBar = ({ value }) => {
  const segments = 10;
  const filled = Math.round((value / 100) * segments);
  return (
    <div className="flex gap-1" data-testid="confidence-bar">
      {Array.from({ length: segments }).map((_, i) => (
        <div
          key={i}
          className="h-2 flex-1"
          style={{
            background: i < filled ? (value >= 80 ? "#D4AF37" : "#fff") : "#2a2a2a",
          }}
        />
      ))}
    </div>
  );
};

export default function SizeFinder({ compact = false }) {
  const [gender, setGender] = useState("male");
  const [fit, setFit] = useState("regular");
  const [age, setAge] = useState("");
  const [height, setHeight] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [weight, setWeight] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const height_cm = toCm(height, "ft", heightIn);
    const weight_kg = parseFloat(weight);
    if (!height_cm || !weight_kg || !age) {
      setError("Please fill in your age, height and weight.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/suggest", {
        height_cm,
        weight_kg,
        age: parseInt(age, 10),
        gender,
        fit,
      });
      setResult(data);
    } catch (err) {
      setError("We couldn't calculate your size. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError("");
  };

  const inputCls =
    "w-full bg-[#141414] border border-[#333] px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-white transition-colors duration-200";
  const unitTag = "flex items-center px-3 bg-[#141414] border-y border-r border-[#333] text-white/50 text-xs font-bold uppercase tracking-wider";

  return (
    <div
      className={`bg-[#0A0A0A] text-white border border-[#333] ${compact ? "p-6" : "p-6 sm:p-8"}`}
      data-testid="size-finder"
    >
      <div className="flex items-center gap-2 mb-1">
        <Crown className="w-4 h-4 gold" strokeWidth={1.5} />
        <span className="overline text-white/50">Premium Fit · AI Sizing</span>
      </div>

      <AnimatePresence mode="wait">
        {!result ? (
          <motion.form
            key="form"
            onSubmit={submit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mt-3"
          >
            <h2 className="font-display text-3xl sm:text-4xl uppercase leading-none mb-6">
              Find Your Size
            </h2>

            <label className="overline text-white/50 block mb-2">Gender</label>
            <Segmented options={GENDERS} value={gender} onChange={setGender} testPrefix="gender" />

            <div className="grid grid-cols-2 gap-4 mt-5">
              <div>
                <label className="overline text-white/50 block mb-2">Age</label>
                <input
                  data-testid="age-input"
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="25"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="overline text-white/50 block mb-2">Weight</label>
                <div className="flex">
                  <input
                    data-testid="weight-input"
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="70"
                    className={inputCls + " border-r-0"}
                  />
                  <span className={unitTag} data-testid="weight-unit-kg">kg</span>
                </div>
              </div>
            </div>

            <div className="mt-5">
              <label className="overline text-white/50 block mb-2">Height</label>
              <div className="flex">
                <input
                  data-testid="height-ft-input"
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="5"
                  className={inputCls + " border-r-0"}
                />
                <span className={unitTag}>ft</span>
                <input
                  data-testid="height-in-input"
                  type="number"
                  value={heightIn}
                  onChange={(e) => setHeightIn(e.target.value)}
                  placeholder="9"
                  className={inputCls + " border-x-0"}
                />
                <span className={unitTag}>in</span>
              </div>
            </div>

            <div className="mt-5">
              <label className="overline text-white/50 block mb-2">Fit Preference</label>
              <Segmented options={FITS} value={fit} onChange={setFit} testPrefix="fit" />
            </div>

            {error && (
              <p className="text-red-400 text-sm mt-4" data-testid="finder-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="size-finder-submit-button"
              className="mt-6 w-full bg-white text-black font-bold uppercase tracking-wider py-4 flex items-center justify-center gap-2 hover:bg-gold transition-colors duration-200 active:scale-[0.99] disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Calculating
                </>
              ) : (
                <>
                  Get My Size <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </motion.form>
        ) : (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-3"
            data-testid="size-result"
          >
            <span className="overline text-white/50">Your Recommended Size</span>
            <div className="flex items-end gap-3 mt-1 mb-4 border-b border-[#333] pb-5">
              <span className="font-display text-7xl leading-none gold" data-testid="result-size">
                {result.size}
              </span>
              <span className="text-white/50 text-sm mb-2">
                Chest {result.chest}
                {result.unit} · Length {result.length}
                {result.unit}
              </span>
            </div>

            <div className="flex items-center justify-between mb-2">
              <span className="overline text-white/50">Match Confidence</span>
              <span className="font-bold text-sm gold" data-testid="confidence-value">
                {result.confidence}%
              </span>
            </div>
            <ConfidenceBar value={result.confidence} />

            <div className="mt-5 bg-[#141414] border border-[#333] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 gold" strokeWidth={1.5} />
                <span className="overline text-white/50">AI Fit Insight</span>
              </div>
              <p className="text-sm text-white/80 leading-relaxed" data-testid="ai-explanation">
                {result.explanation}
              </p>
            </div>

            <button
              onClick={reset}
              data-testid="try-again-button"
              className="mt-5 w-full border border-[#333] text-white font-bold uppercase tracking-wider py-3 flex items-center justify-center gap-2 hover:border-white transition-colors duration-200"
            >
              <RotateCcw className="w-4 h-4" /> Try Again
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
